'use client';

import { BLOCKS, Document } from '@contentful/rich-text-types';
import { slugify } from '@/layers/support/utils/slugify';

interface TocHeading {
  text: string;
  id: string;
  level: 2 | 3;
}

interface TableOfContentsProps {
  document: Document;
}

function extractText(node: any): string {
  if (node.nodeType === 'text') return node.value || '';
  if (Array.isArray(node.content)) return node.content.map(extractText).join('');
  return '';
}

function extractHeadings(document: Document): TocHeading[] {
  const headings: TocHeading[] = [];

  for (const node of document.content) {
    if (node.nodeType === BLOCKS.HEADING_2 || node.nodeType === BLOCKS.HEADING_3) {
      const text = extractText(node);
      if (text) {
        headings.push({
          text,
          id: slugify(text),
          level: node.nodeType === BLOCKS.HEADING_2 ? 2 : 3,
        });
      }
    }
  }

  return headings;
}

const TableOfContents: React.FC<TableOfContentsProps> = ({ document }) => {
  const headings = extractHeadings(document);

  // Don't show TOC if fewer than 2 headings
  if (headings.length < 2) {
    return null;
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = window.document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav
      aria-label="Table of contents"
      className="bg-secondary/40 rounded-xl p-4 border border-secondary"
    >
      <p className="text-[10px] font-semibold uppercase tracking-widest text-primary/40 mb-3">
        Contents
      </p>
      <div className="space-y-2">
        {headings.map((heading, index) => (
          <a
            key={heading.id}
            href={`#${heading.id}`}
            onClick={e => handleClick(e, heading.id)}
            className="group flex items-start gap-2 text-[12px] leading-snug text-primary/60 hover:text-primary transition-colors"
          >
            <span className="text-[10px] text-primary/30 font-mono mt-0.5 group-hover:text-primary/50 transition-colors shrink-0">
              {String(index + 1).padStart(2, '0')}
            </span>
            <span className="line-clamp-2">{heading.text}</span>
          </a>
        ))}
      </div>
    </nav>
  );
};

export default TableOfContents;
